import { fraxlendPairsFor } from '@1delta/data-sdk'
import { FraxlendPairAbi } from '@1delta/abis'
import {
  FetcherResult,
  MulticallCall,
  OraclePriceEntry,
  ParseContext,
} from '../types'
import { createMarketUid } from '../../../utils'
import { fraxlendLenderKey } from '../../../lending/public-data/fraxlend/convertPublic'

/**
 * Fraxlend oracle price fetcher.
 *
 * Each `FraxlendPair` carries its OWN oracle valuing the collateral in ASSET
 * terms — and it is not merely *a* price, it is **the price the pair liquidates
 * against**. Publishing anything else would mean our health factors disagreed
 * with the chain's.
 *
 * Why a dedicated fetcher, on two independent grounds (either alone suffices
 * per `AGENTS.md`):
 *
 *  1. Fraxlend liquidates against this feed rather than a shared one; and
 *  2. its pairs list collateral nothing else on the chain prices — `FXB_20261231`
 *     (a Frax bond), `srRoyUSDC`, `WFRAX`. Without this those markets report
 *     blank or wrong `depositsUSDOracle` / `debtUSDOracle`.
 *
 * DERIVED (Pass 2), like Morpho/Midnight/Teller: the collateral USD needs the
 * ASSET's USD from an earlier Pass-1 parser. Parsed with `updatePrices=false`
 * so derived values never overwrite direct oracle reads.
 *
 * ## Three traps live in this one number
 *
 * 1. **`exchangeRate` is COLLATERAL-PER-ASSET — inverted** relative to every
 *    other lender here.
 * 2. **It is NOT decimal-normalised.** Inverting it into a human price needs
 *    `x 10^(collateralDecimals - assetDecimals)`; on frxUSD/WBTC (18/8)
 *    omitting that is wrong by **1e10**. (The pair's own solvency math must NOT
 *    apply the term — same number, two different correct treatments.)
 * 3. **Read `updateExchangeRate()`, NEVER the stored `exchangeRateInfo`.** The
 *    stored rate only moves when someone touches the pair, and this protocol is
 *    quiet enough that it runs MONTHS stale — measured 2026-08-11 at 36 days /
 *    -0.39 %, 31 days / -0.35 % and **134 days / -0.98 %** on the three
 *    leverage-enabled pairs. `updateExchangeRate` mutates on-chain but under
 *    `eth_call` returns the freshly computed rate without persisting, which is
 *    why it is declared `view` in our ABI.
 */

/** Grouped metadata for one pair (one rate call). */
interface FraxlendPairMeta {
  pair: string
  /** The borrowable / lent token — the BASE the collateral is quoted in. */
  asset: string
  /** The posted-collateral token — the derived side. */
  collateral: string
}

/**
 * Build multicall calls for Fraxlend oracles: one `FetcherResult` per curated
 * pair, with three calls — the FRESH rate plus both leg addresses.
 *
 * The legs are read rather than taken from config because the config carries
 * only pair addresses, and `asset()` / `collateralContract()` are immutable for
 * the life of a pair, so there is nothing to go stale.
 *
 * `lender` is the pair's `FRAXLEND_<chainId>_<PAIR>` key so entries join the
 * lending data emitted by `convertFraxlendPairsToResponse`.
 */
export function getFraxlendCalls(
  chainId: string,
): FetcherResult<FraxlendPairMeta>[] {
  const pairs = fraxlendPairsFor('FRAXLEND', chainId)
  if (pairs.length === 0) return []

  const results: FetcherResult<FraxlendPairMeta>[] = []
  for (const p of pairs) {
    if (!p?.address) continue
    const calls: MulticallCall[] = [
      // FRESH, not stored — see trap 3 in the header.
      { address: p.address, name: 'updateExchangeRate', params: [] },
      { address: p.address, name: 'asset', params: [] },
      { address: p.address, name: 'collateralContract', params: [] },
      { address: p.address, name: 'EXCHANGE_PRECISION', params: [] },
    ]
    results.push({
      calls,
      meta: {
        pair: p.address.toLowerCase(),
        // Filled in by the parser from the reads below; the config has neither.
        asset: '',
        collateral: '',
      },
      lender: fraxlendLenderKey('FRAXLEND', chainId, p.address),
    })
  }
  return results
}

/**
 * Parse Fraxlend oracle results.
 *
 * `data[0]` = `updateExchangeRate()` -> `(isBorrowAllowed, low, high)`. We take
 * the HIGH rate, which is the side the pair values collateral with for a borrow
 * (the conservative one) — collapsing the two-sided band to a single number is
 * a real loss, but a price map has one slot, and the conservative side is the
 * right one to publish.
 *
 * Emits an ASSET entry (price = 1, the oracle's own unit, `staticBase`) plus the
 * derived COLLATERAL entry, per the `baseAsset`/`staticBase` rule.
 */
export function parseFraxlendResults(
  data: any[],
  meta: FraxlendPairMeta,
  context: ParseContext,
): OraclePriceEntry[] {
  const { chainId, usdPrices, tokenList } = context
  const entries: OraclePriceEntry[] = []

  const rateTuple = data?.[0]
  const assetRaw = data?.[1]
  const collateralRaw = data?.[2]
  const precisionRaw = data?.[3]
  if (typeof assetRaw !== 'string' || typeof collateralRaw !== 'string')
    return entries

  const asset = assetRaw.toLowerCase()
  const collateral = collateralRaw.toLowerCase()
  const lenderKey = fraxlendLenderKey('FRAXLEND', chainId, meta.pair)

  const usdOf = (addr: string): number | undefined => {
    const key = tokenList?.[addr]?.assetGroup ?? `${chainId}-${addr}`
    return usdPrices[key] ?? usdPrices[addr]
  }
  const assetUSD = usdOf(asset)
  // Without the base asset's USD there is nothing to derive from. Every pair's
  // asset is a mainstream stable (frxUSD / FRAX / crvUSD / DOLA), so this is
  // the "Pass 1 has not run" case, not a real gap.
  if (!assetUSD) return entries

  // `(isBorrowAllowed, lowExchangeRate, highExchangeRate)` — take HIGH.
  const high = Array.isArray(rateTuple) ? rateTuple[2] : rateTuple?.[2]
  if (high == null) return entries
  let rate: bigint
  let precision: bigint
  try {
    rate = BigInt(high)
    precision = precisionRaw != null ? BigInt(precisionRaw) : 10n ** 18n
  } catch {
    return entries
  }
  if (rate <= 0n || precision <= 0n) return entries

  const assetDecimals = tokenList?.[asset]?.decimals ?? 18
  const collateralDecimals = tokenList?.[collateral]?.decimals ?? 18

  // INVERT (collateral-per-asset -> asset-per-collateral) AND correct decimals.
  // Verified live on three decimal shapes: frxUSD/sfrxETH (18/18) -> 2162.25,
  // frxUSD/WBTC (18/**8**) -> 60,386.47, frxUSD/sfrxUSD (18/18) -> 1.2009.
  const assetPerCollateral =
    (Number(precision) / Number(rate)) *
    10 ** (collateralDecimals - assetDecimals)
  if (!(assetPerCollateral > 0) || !Number.isFinite(assetPerCollateral))
    return entries

  entries.push({
    asset,
    price: 1,
    priceUSD: assetUSD,
    marketUid: createMarketUid(chainId, lenderKey, asset),
    targetLender: lenderKey,
    description: 'Fraxlend pair asset',
    staticBase: true,
    baseAsset: asset,
  })
  entries.push({
    asset: collateral,
    price: assetPerCollateral,
    priceUSD: assetPerCollateral * assetUSD,
    marketUid: createMarketUid(chainId, lenderKey, collateral),
    targetLender: lenderKey,
    description: 'Fraxlend collateral (pair oracle, liquidation price)',
    baseAsset: asset,
  })

  return entries
}

/** ABI for the Fraxlend oracle calls. */
export function getFraxlendAbi() {
  return FraxlendPairAbi
}

/** Fraxlend Oracle Fetcher module. */
export const fraxlendFetcher = {
  getCalls: getFraxlendCalls,
  parse: parseFraxlendResults,
  getAbi: getFraxlendAbi,
}
